// src/components/CompanyBookingForm.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import emailjs from '@emailjs/browser';
import { auth, db } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import DatePicker from 'react-multi-date-picker';
import './CompanyBookingForm.css';

export default function CompanyBookingForm({ companyId, companyName }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    guestCount: '',
    dates: [],
    budget: '',
    venue: '',
    message: '',
  });

  const [success, setSuccess] = useState(false);
  const [checking, setChecking] = useState(false);
  const [dateFeedback, setDateFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastCheckedDatesRef = useRef([]);

  const formatDate = (dateObj) => dateObj.format('DD/MM/YYYY');

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setFormData((prev) => ({
          ...prev,
          email: user.email || '',
          name: user.displayName || '',
        }));
      }
    });
    return () => unsubscribe();
  }, []);

  const checkDatesAvailability = useCallback(async (datesToCheck) => {
    if (!datesToCheck || !Array.isArray(datesToCheck) || datesToCheck.length === 0) {
      setDateFeedback('');
      return;
    }

    setChecking(true);
    try {
      const formattedDates = datesToCheck.map(d => formatDate(d));
      const available = [];
      const taken = [];

      for (const date of formattedDates) {
        const q = query(
          collection(db, 'company_bookings'),
          where('companyId', '==', companyId),
          where('date', '==', date)
        );
        const snapshot = await getDocs(q);
        if (snapshot.empty) {
          available.push(date);
        } else {
          taken.push(date);
        }
      }

      const validDates = datesToCheck.filter(d => available.includes(formatDate(d)));

      setFormData(prev => ({
        ...prev,
        dates: validDates
      }));

      if (taken.length > 0 && available.length > 0) {
        setDateFeedback(`⚠️ Les dates suivantes ont été retirées car elles sont déjà réservées : ${taken.join(', ')}`);
      } else if (taken.length > 0 && available.length === 0) {
        setDateFeedback('❌ Toutes les dates sélectionnées sont déjà réservées.');
      } else {
        setDateFeedback('');
      }
    } catch (error) {
      console.error('Erreur vérification dates:', error);
    } finally {
      setChecking(false);
    }
  }, [companyId]);

  const handleDateChange = (dates) => {
    const safeDates = dates || [];
    setFormData(prev => ({ ...prev, dates: safeDates }));
    setDateFeedback('');
    setSuccess(false);
  };

  const handleDatePickerClose = () => {
    const currentDates = formData.dates || [];
    if (!Array.isArray(currentDates)) return;
    
    const currentFormatted = currentDates.map(d => formatDate(d)).sort();
    const lastFormatted = [...lastCheckedDatesRef.current].sort();

    const hasChanged = currentFormatted.length !== lastFormatted.length ||
      currentFormatted.some((date, i) => date !== lastFormatted[i]);

    if (hasChanged && currentDates.length > 0) {
      lastCheckedDatesRef.current = [...currentFormatted];
      checkDatesAvailability(currentDates);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    if (success) setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const currentDates = formData.dates || [];
    if (!Array.isArray(currentDates) || currentDates.length === 0) {
      alert("Veuillez sélectionner au moins une date pour votre événement.");
      return;
    }

    setIsSubmitting(true);

    const formattedDates = currentDates.map(d => formatDate(d));

    const templateParams = {
      companyName,
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      eventType: formData.eventType,
      guestCount: formData.guestCount,
      dates: formattedDates.join(', '),
      budget: formData.budget,
      venue: formData.venue,
      message: formData.message,
      // Pour identifier qu'il s'agit d'une demande pour une société
      requestType: 'Société d\'événementiel'
    };

    try {
      await emailjs.send(
        'SERVICE_ID', // Remplacez par votre Service ID
        'template_company_booking', // Nouveau template ID spécialisé pour les sociétés
        templateParams,
        'udMka3n0REP1MEHEo' // Votre Public Key
      );
      
      setSuccess(true);
      setFormData({
        name: auth.currentUser?.displayName || '',
        email: auth.currentUser?.email || '',
        phone: '',
        eventType: '',
        guestCount: '',
        dates: [],
        budget: '',
        venue: '',
        message: '',
      });
      setDateFeedback('');
      lastCheckedDatesRef.current = [];
    } catch (error) {
      console.error('Erreur EmailJS:', error);
      alert("Une erreur est survenue lors de l'envoi. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const safeDates = formData.dates || [];
  const datesLength = Array.isArray(safeDates) ? safeDates.length : 0;

  return (
    <div className="company-booking-form">
      <h3>Demander un devis à {companyName}</h3>

      {success && (
        <p className="success-msg">✅ Votre demande a été envoyée avec succès ! Nous vous recontacterons rapidement.</p>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <input
            type="text"
            name="name"
            placeholder="Votre nom complet *"
            value={formData.name}
            onChange={handleChange}
            required
          />

          <input
            type="email"
            name="email"
            placeholder="Votre email *"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-row">
          <input
            type="tel"
            name="phone"
            placeholder="Votre numéro de téléphone *"
            value={formData.phone}
            onChange={handleChange}
            required
          />

          <select
            name="eventType"
            value={formData.eventType}
            onChange={handleChange}
            required
          >
            <option value="">Type d'événement *</option>
            <option value="mariage">Mariage</option>
            <option value="corporate">Événement corporatif</option>
            <option value="birthday">Anniversaire</option>
            <option value="gala">Gala / Soirée de prestige</option>
            <option value="conference">Conférence / Séminaire</option>
            <option value="festival">Festival / Concert</option>
            <option value="other">Autre</option>
          </select>
        </div>

        <div className="form-row">
          <input
            type="number"
            name="guestCount"
            placeholder="Nombre d'invités approximatif"
            value={formData.guestCount}
            onChange={handleChange}
            min="1"
          />

          <select
            name="budget"
            value={formData.budget}
            onChange={handleChange}
          >
            <option value="">Budget approximatif</option>
            <option value="moins-5000">Moins de 5 000 DH</option>
            <option value="5000-15000">5 000 - 15 000 DH</option>
            <option value="15000-30000">15 000 - 30 000 DH</option>
            <option value="30000-50000">30 000 - 50 000 DH</option>
            <option value="50000-100000">50 000 - 100 000 DH</option>
            <option value="plus-100000">Plus de 100 000 DH</option>
          </select>
        </div>

        <input
          type="text"
          name="venue"
          placeholder="Lieu de l'événement (si déjà défini)"
          value={formData.venue}
          onChange={handleChange}
        />

        <label htmlFor="dates">Dates souhaitées pour votre événement *</label>
        <DatePicker
          multiple
          value={safeDates}
          onChange={handleDateChange}
          onClose={handleDatePickerClose}
          format="DD/MM/YYYY"
          placeholder="Sélectionnez les dates possibles"
          minDate={new Date()}
          weekDays={['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa']}
          months={['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']}
          arrow={true}
          containerStyle={{ width: "100%" }}
          calendarProps={{
            style: {
              backgroundColor: "#1e1e1e",
              border: "1px solid #333",
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
              padding: window.innerWidth <= 600 ? "12px" : "16px",
              marginTop: "8px",
              overflow: "hidden"
            }
          }}
          mapDays={({ date, today, selectedDate, isSameDate }) => {
            let props = {
              style: {
                color: "#e0e0e0",
                borderRadius: "8px",
                transition: "all 0.2s ease",
                margin: "2px",
                fontWeight: "500"
              }
            };

            if (date.format() === today.format()) {
              props.style = {
                ...props.style,
                backgroundColor: "#333",
                color: "#ff9800",
                fontWeight: "600"
              };
            }

            const safeSelectedDate = Array.isArray(selectedDate) ? selectedDate : [];
            if (safeSelectedDate.some(d => isSameDate(date, d))) {
              props.style = {
                ...props.style,
                backgroundColor: "#ff9800",
                color: "#121212",
                fontWeight: "600",
                boxShadow: "0 2px 8px rgba(255, 152, 0, 0.3)"
              };
            }

            return props;
          }}
          renderButton={(direction, handleClick) => (
            <button 
              type="button"
              onClick={handleClick}
              className="date-picker-nav-btn"
              style={{
                background: '#2c2c2c',
                border: '1px solid #444',
                borderRadius: '8px',
                width: window.innerWidth <= 600 ? '36px' : '40px',
                height: window.innerWidth <= 600 ? '36px' : '40px',
                cursor: 'pointer',
                color: '#ff9800',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: window.innerWidth <= 600 ? '18px' : '20px',
                fontWeight: 'bold',
                transition: 'all 0.2s ease',
                margin: '0 4px',
                padding: '0'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#ff9800';
                e.target.style.color = '#121212';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#2c2c2c';
                e.target.style.color = '#ff9800';
                e.target.style.transform = 'scale(1)';
              }}
            >
              {direction === "right" ? "›" : "‹"}
            </button>
          )}
        />

        {checking && <div className="checking-msg">Vérification des dates disponibles...</div>}
        {dateFeedback && <div className="date-feedback-msg">{dateFeedback}</div>}

        <textarea
          name="message"
          placeholder="Décrivez votre projet en détail : thème, services souhaités, exigences particulières..."
          value={formData.message}
          onChange={handleChange}
          rows={5}
          required
        />

        <button 
          type="submit" 
          disabled={checking || datesLength === 0 || isSubmitting}
        >
          {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande de devis'}
        </button>
      </form>
    </div>
  );
}